import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { isArray } from "lodash";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { BsChevronDown, BsChevronRight, BsFolder2, BsPencil, BsPlusLg, BsTrash } from "react-icons/bs";
import { LoginContext } from "helpers/LoginContext";
import TableHeader from "components/common/Table/TableHeader";
import CustomButton from "components/common/Button/CustomButton";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { classNames, parseBooleanVal } from "Utils/utils";
import SideBarNew from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import Breadcrumb from "components/common/Breadcrumb";
import {
  SETTINGS_BREADCRUMB_PAGES,
  SP_SETTING_BREADCRUMB,
} from "Constants/BreadcrumbConstant";
import { generalActions } from "store/actions";
import { TOAST_TYPE } from "Constants/constant";
import {
  AddDocumentModal,
  ComplianceSummaryBar,
  ParticipantComplianceDocumentsSection,
  ParticipantComplianceDocumentDragOverlay,
  EditDocumentModal,
  ParticipantFormsSection,
  ParticipantFormFilters,
  useParticipantFormCatalog,
  buildDisplayForms,
  getFormKey,
  InstructionCard,
} from "components/StaffCompliance";
import Search from "components/common/Search/Search";
import SweetAlertConfirmationModal from "components/common/SweetAlertConfirmation/SweetAlertConfirmation";
import SelectOption from "components/common/Select/Select";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import InputField from "components/common/Input/Input";
import { apiGetRequest, apiPostRequest, apiPutRequest, apiDeleteRequest } from "helpers/Requests";
import {
  URL_SP_PARTICIPANT_COMPLIANCE_PROFILES,
  URL_SP_PARTICIPANT_COMPLIANCE_PROFILE,
  URL_SP_PARTICIPANT_COMPLIANCE_PROFILE_DELETE_PREVIEW,
  URL_SP_PARTICIPANT_COMPLIANCE_UPDATE_DOCUMENT,
} from "store/services/URL";
import { toast } from "react-toastify";
import { useNavigate, useParams, useLocation, useNavigationType } from "react-router";

const stripDefaultSuffix = (label) => label?.replace(/\s*\(Default\)$/, "") ?? "";

const getDefaultProfile = (profileList) =>
  profileList.find((p) => p.is_default) || profileList[0];

const normalizeComplianceSettingsForUpdate = (items = []) =>
  items.map((item) => ({
    ...item,
    type: item.type ?? (item.sp_form_template_id != null ? "form" : "document"),
    score: Math.round(Number(item.score) || 0),
    name:
      typeof item.name === "string"
        ? item.name.trim()
        : item.name,
  }));

const buildComplianceUpdatePayload = (documents = [], forms = []) => ({
  participant_compliance_settings: normalizeComplianceSettingsForUpdate([
    ...documents,
    ...forms,
  ]),
});

const COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH = 500;

const ComplianceProfileDescriptionField = ({
  id,
  name,
  value,
  onChange,
  disabled = false,
  readOnly = false,
}) => {
  const length = (value || "").length;
  const atLimit = length >= COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        Description (optional, maximum {COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH}{" "}
        characters)
      </label>
      <textarea
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        maxLength={COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH}
        rows={4}
        placeholder="Brief description of this compliance template"
        className={classNames(
          "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm",
          (disabled || readOnly) && "bg-[#4b6bb710] cursor-not-allowed"
        )}
      />
      <span
        className={classNames(
          "mt-1 block text-xs tabular-nums",
          atLimit ? "text-red-500" : "text-gray-500"
        )}
      >
        {length} / {COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH} characters
      </span>
    </div>
  );
};

const ParticipantCompliance = ({
  spParticipantComplianceSettingsRequest,
  spParticipantComplianceSetting,
  spParticipantComplianceLoader,
  spParticipantComplianceSettingsUpdateRequest,
  spParticipantComplianceUpdateLoader,
  spParticipantComplianceSettingUpdateError,
  spParticipantComplianceAddDocumentRequest,
  spParticipantComplianceUpdateDocumentRequest,
  spParticipantComplianceDeleteDocumentRequest,
  spParticipantComplianceAddDocumentCompleted,
  spParticipantComplianceAddDocumentClear,
  spParticipantComplianceUpdateDocumentCompleted,
  spParticipantComplianceUpdateDocumentClear,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { profileUuid } = useParams();
  const location = useLocation();
  const { isUserGracePeriodOver, sideBarPadding } = useContext(LoginContext);

  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [editProfileName, setEditProfileName] = useState("");
  const [editProfileDescription, setEditProfileDescription] = useState("");
  const [editProfileIsDefault, setEditProfileIsDefault] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [deleteProfileWarning, setDeleteProfileWarning] = useState(null);
  const [deletingProfile, setDeletingProfile] = useState(false);

  const selectedProfileOption = profiles.find((p) => p.value === profileUuid);
  const isDefaultProfile = Boolean(selectedProfileOption?.is_default);
  const assignedCount = selectedProfileOption?.assigned_participants_count ?? 0;
  const isDeleteDisabled =
    isDefaultProfile || assignedCount > 0 || deletingProfile;
  const deleteTooltip = isDefaultProfile
    ? "The default compliance profile cannot be deleted."
    : assignedCount > 0
    ? "Assigned compliance can't be deleted."
    : "Delete compliance profile";
  const profileName = selectedProfileOption?.label || location.state?.profileName || "Compliance";

  const [lastErrorLocation, setLastErrorLocation] = useState([]);

  const [compliancesData, setCompliancesData] = useState({
    document: [],
    form: [],
  });

  const [activeDragId, setActiveDragId] = useState(null);
  const [documentsExpanded, setDocumentsExpanded] = useState(true);
  const [formsExpanded, setFormsExpanded] = useState(true);
  const [preserveFormCatalogOrder, setPreserveFormCatalogOrder] = useState(false);
  const [formOrderKeys, setFormOrderKeys] = useState(null);
  const [formSearch, setFormSearch] = useState("");
  const [documentSearch, setDocumentSearch] = useState("");
  const [formCategoryFilter, setFormCategoryFilter] = useState([]);
  const hasFormFilter =
    formSearch.trim().length > 0 || (formCategoryFilter?.length ?? 0) > 0;
  const hasDocumentFilter = documentSearch.trim().length > 0;
  const {
    categoryOptions: formCategoryOptions,
    catalogForms,
    catalogLoading,
  } = useParticipantFormCatalog({
    search: formSearch,
    selectedCategories: formCategoryFilter,
  });
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [isAddDocumentSubmitting, setIsAddDocumentSubmitting] = useState(false);
  // TODO(STAFF_COMPLIANCE_DELETE): FE-6/A10,A11 – Delete document: confirm modal + loading. Cleared when spParticipantComplianceSetting updates.
  const [deleteDocumentConfirmItem, setDeleteDocumentConfirmItem] = useState(null);
  const [deleteDocumentLoadingId, setDeleteDocumentLoadingId] = useState(null);
  const [editDocumentItem, setEditDocumentItem] = useState(null);
  const [isEditDocumentSubmitting, setIsEditDocumentSubmitting] = useState(false);
  const addDocumentSubmittingRef = useRef(false);
  const editDocumentSubmittingRef = useRef(false);
  const dateToggleRequestRef = useRef(0);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const loadProfiles = async () => {
    setProfilesLoading(true);
    try {
      const res = await apiGetRequest(URL_SP_PARTICIPANT_COMPLIANCE_PROFILES);
      const body = res?.data ?? res;
      const list = (body?.profiles || []).map((p) => ({
        value: p.uuid,
        label: p.is_default ? `${p.name} (Default)` : p.name,
        description: p.description,
        is_default: p.is_default,
        assigned_participants_count: p.assigned_participants_count ?? 0,
      }));
      setProfiles(list);
    } catch (e) {
      toast.error(e?.message || "Failed to load compliance profiles");
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, []);

  useEffect(() => {
    if (profilesLoading || profiles.length === 0) return;

    const defaultProfile = getDefaultProfile(profiles);
    if (!defaultProfile) return;

    // POP = initial load or browser refresh; PUSH/REPLACE = in-app navigation (e.g. create or dropdown).
    const isHardReload =
      navigationType === "POP" &&
      profileUuid &&
      profileUuid !== defaultProfile.value;

    if (!profileUuid) {
      navigate(
        `/settings/participant-compliance-setting/${defaultProfile.value}`,
        {
          replace: true,
          state: {
            profileName: stripDefaultSuffix(defaultProfile.label),
          },
        }
      );
      return;
    }

    if (isHardReload) {
      navigate(
        `/settings/participant-compliance-setting/${defaultProfile.value}`,
        {
          replace: true,
          state: {
            profileName: stripDefaultSuffix(defaultProfile.label),
          },
        }
      );
    }
  }, [profileUuid, profiles, profilesLoading, navigate, navigationType]);

  useEffect(() => {
    if (profileUuid) {
      spParticipantComplianceSettingsRequest(profileUuid);
    }
  }, [profileUuid, spParticipantComplianceSettingsRequest]);

  useEffect(() => {
    if (!spParticipantComplianceSettingUpdateError || !profileUuid) return;
    spParticipantComplianceSettingsRequest(profileUuid);
  }, [
    spParticipantComplianceSettingUpdateError,
    profileUuid,
    spParticipantComplianceSettingsRequest,
  ]);

  const awaitingProfileSelection =
    !profileUuid && (profilesLoading || profiles.length > 0);
  const hasNoProfiles = !profileUuid && !profilesLoading && profiles.length === 0;

  const handleProfileChange = (option) => {
    if (!option?.value || option.value === profileUuid) return;
    navigate(`/settings/participant-compliance-setting/${option.value}`, {
      state: {
        profileName: stripDefaultSuffix(option.label),
      },
      replace: true,
    });
  };

  const handleCreateCompliance = async () => {
    const name = newName.trim();
    const description = newDescription.trim();
    if (!name) return;
    if (description.length > COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH) {
      toast.error(
        `Description must not exceed ${COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH} characters.`
      );
      return;
    }
    setCreating(true);
    try {
      const res = await apiPostRequest(URL_SP_PARTICIPANT_COMPLIANCE_PROFILES, {
        name,
        description: description || undefined,
      });
      const body = res?.data ?? res;
      const profile = body?.data?.profile ?? body?.profile;
      setCreateOpen(false);
      setNewName("");
      setNewDescription("");
      toast.success("Compliance profile created");
      await loadProfiles();
      if (profile?.uuid) {
        navigate(`/settings/participant-compliance-setting/${profile.uuid}`, {
          state: {
            profileName: profile.name,
          },
        });
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to create compliance"
      );
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (spParticipantComplianceSetting) {
      setCompliancesData({
        document: spParticipantComplianceSetting.document || [],
        form: spParticipantComplianceSetting.form || [],
      });
      setDeleteDocumentLoadingId(null);
      setPreserveFormCatalogOrder(false);
      setFormOrderKeys(null);
    }
  }, [spParticipantComplianceSetting]);

  useEffect(() => {
    if (spParticipantComplianceAddDocumentCompleted == null) return;
    addDocumentSubmittingRef.current = false;
    setIsAddDocumentSubmitting(false);
    if (spParticipantComplianceAddDocumentCompleted?.success) {
      setAddDocumentModalOpen(false);
    }
    dispatch(spParticipantComplianceAddDocumentClear());
  }, [spParticipantComplianceAddDocumentCompleted, dispatch, spParticipantComplianceAddDocumentClear]);

  useEffect(() => {
    if (spParticipantComplianceUpdateDocumentCompleted == null) return;
    editDocumentSubmittingRef.current = false;
    setIsEditDocumentSubmitting(false);
    if (spParticipantComplianceUpdateDocumentCompleted?.success) {
      setEditDocumentItem(null);
    }
    dispatch(spParticipantComplianceUpdateDocumentClear());
  }, [
    spParticipantComplianceUpdateDocumentCompleted,
    dispatch,
    spParticipantComplianceUpdateDocumentClear,
  ]);

  const handleDiscard = () => {
    if (spParticipantComplianceSetting) {
      setCompliancesData({
        document: spParticipantComplianceSetting.document || [],
        form: spParticipantComplianceSetting.form || [],
      });
      setLastErrorLocation([]);
      setPreserveFormCatalogOrder(false);
      setFormOrderKeys(null);
    }
  };

  const handleSubmit = () => {
    if (isUserGracePeriodOver || lastErrorLocation.length > 0) {
      return;
    }
    spParticipantComplianceSettingsUpdateRequest(
      buildComplianceUpdatePayload(
        compliancesData?.document || [],
        compliancesData?.form || []
      ),
      profileUuid
    );
  };

  const handleSwitchChange = (complianceType, item, value, type = "score") => {
    if (complianceType === "form" && type === "switch") {
      const currentOrder = buildDisplayForms(
        catalogForms,
        compliancesData.form || [],
        hasFormFilter,
        formSearch,
        preserveFormCatalogOrder,
        preserveFormCatalogOrder ? formOrderKeys : null
      ).map(getFormKey);
      setFormOrderKeys(currentOrder);
      setPreserveFormCatalogOrder(true);
    }

    const prevSwitches = _.cloneDeep(compliancesData);
    if (prevSwitches?.[`${complianceType}`]) {
      if (!isArray(prevSwitches?.[`${complianceType}`])) {
        Object.assign(prevSwitches?.[`${complianceType}`], []);
      }
      const findItemIndex = (list) =>
        list.findIndex(
          (x) =>
            (item.id != null && x.id === item.id) ||
            (item.sp_form_template_id != null &&
              Number(x.sp_form_template_id) === Number(item.sp_form_template_id))
        );

      const index = findItemIndex(prevSwitches[`${complianceType}`] || []);

      if (index !== -1) {
        if (type === "score") {
          prevSwitches[`${complianceType}`][index].score = Number(value);
        } else if (type === "require_issue_date" || type === "require_expiry_date") {
          prevSwitches[`${complianceType}`][index][type] = value;
        } else {
          prevSwitches[`${complianceType}`][index].is_compliance = value;
        }
      } else if (complianceType === "form" && item.sp_form_template_id) {
        prevSwitches.form.push({
          sp_form_template_id: item.sp_form_template_id,
          name: item.name,
          type: "form",
          is_compliance: type === "switch" ? value : false,
          score: type === "score" ? Number(value) : 0,
          category_name: item.category_name || null,
        });
      }
    }
    // else {
    //   if (type === "score") {
    //     Object.assign(prevSwitches, {
    //       [`${complianceType}`]: [
    //         { id: item.id, score: Number(value), name: item.name },
    //       ],
    //     });
    //   } else {
    //     Object.assign(prevSwitches, {
    //       [`${complianceType}`]: [
    //         { id: item.id, is_compliance: value, name: item.name },
    //       ],
    //     });
    //   }
    // }

    setCompliancesData({ ...prevSwitches });
  };

  const handleDateRequirementChange = async (item, field, value) => {
    handleSwitchChange("document", item, value, field);

    if (!item?.uuid || !profileUuid || isUserGracePeriodOver) {
      return;
    }

    const requestId = ++dateToggleRequestRef.current;

    try {
      await apiPutRequest(URL_SP_PARTICIPANT_COMPLIANCE_UPDATE_DOCUMENT(item.uuid), {
        profile_uuid: profileUuid,
        name: item.name,
        score: Math.round(Number(item.score) || 0),
        is_compliance: !!item.is_compliance,
        require_issue_date:
          field === "require_issue_date" ? value : !!item.require_issue_date,
        require_expiry_date:
          field === "require_expiry_date" ? value : !!item.require_expiry_date,
      });
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to update document setting."
      );
      handleDiscard();
    } finally {
      if (requestId === dateToggleRequestRef.current) {
        dateToggleRequestRef.current = 0;
      }
    }
  };

  const openEditProfile = () => {
    const current = profiles.find((p) => p.value === profileUuid);
    setEditProfileName(profileName.replace(/\s*\(Default\)$/, ""));
    setEditProfileDescription(current?.description || "");
    setEditProfileIsDefault(Boolean(current?.is_default));
    setEditProfileOpen(true);
  };

  const handleSaveProfile = async () => {
    if (isDefaultProfile) return;
    const name = editProfileName.trim();
    const description = editProfileDescription.trim();
    if (!name || !profileUuid) return;
    if (description.length > COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH) {
      toast.error(
        `Description must not exceed ${COMPLIANCE_PROFILE_DESCRIPTION_MAX_LENGTH} characters.`
      );
      return;
    }
    setSavingProfile(true);
    try {
      await apiPutRequest(URL_SP_PARTICIPANT_COMPLIANCE_PROFILE(profileUuid), {
        name,
        description: description || null,
        is_default: editProfileIsDefault,
      });
      toast.success("Compliance profile updated");
      setEditProfileOpen(false);
      await loadProfiles();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to update profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleDeleteProfileClick = async () => {
    if (!profileUuid || isDeleteDisabled) return;
    try {
      const res = await apiGetRequest(
        URL_SP_PARTICIPANT_COMPLIANCE_PROFILE_DELETE_PREVIEW(profileUuid)
      );
      const body = res?.data ?? res;
      setDeleteProfileWarning(body?.data ?? body);
    } catch (e) {
      toast.error(e?.message || "Failed to load delete preview");
    }
  };

  const handleDeleteProfileConfirm = async (confirmed) => {
    if (
      !confirmed ||
      !profileUuid ||
      deleteProfileWarning?.can_delete === false
    ) {
      setDeleteProfileWarning(null);
      return;
    }
    setDeletingProfile(true);
    try {
      await apiDeleteRequest(URL_SP_PARTICIPANT_COMPLIANCE_PROFILE(profileUuid));
      toast.success("Compliance profile deleted");
      setDeleteProfileWarning(null);
      await loadProfiles();
      const defaultProfile =
        profiles.find((p) => p.is_default && p.value !== profileUuid) ||
        profiles.find((p) => p.value !== profileUuid);
      if (defaultProfile) {
        navigate(`/settings/participant-compliance-setting/${defaultProfile.value}`, {
          replace: true,
          state: {
            profileName: stripDefaultSuffix(defaultProfile.label),
          },
        });
      } else {
        navigate("/settings/participant-compliance-setting", { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to delete profile");
    } finally {
      setDeletingProfile(false);
    }
  };

  const calculateTotalPercentage = () => {
    const documentScore = compliancesData?.document
      .filter((doc) => doc.is_compliance)
      .reduce((acc, doc) => acc + doc.score, 0);

    const formScore = compliancesData?.form
      .filter((form) => form.is_compliance)
      .reduce((acc, form) => acc + form.score, 0);

    const totalScore = documentScore + formScore;

    if (lastErrorLocation.length > 0 && Number(totalScore) === 100) {
      setLastErrorLocation([]);
    }
    return totalScore;
  };

  const checkIsAnyComplianceTrueWithZeroScore = () => {
    const data = [...compliancesData.document, ...compliancesData.form];
    return data.some(
      (x) => x.is_compliance === true && Number(x.score) <= 0
    );
  };

  const isSubmitDisabled =
    checkIsAnyComplianceTrueWithZeroScore() ||
    lastErrorLocation.length > 0 ||
    spParticipantComplianceUpdateLoader ||
    isUserGracePeriodOver ||
    Number(calculateTotalPercentage()) !== 100;

  const documents = compliancesData.document || [];
  const sortedFormsSnapshot = useMemo(
    () =>
      buildDisplayForms(
        catalogForms,
        compliancesData.form || [],
        hasFormFilter,
        formSearch,
        false
      ),
    [catalogForms, compliancesData.form, hasFormFilter, formSearch]
  );

  useEffect(() => {
    if (sortedFormsSnapshot.length === 0 || preserveFormCatalogOrder) {
      return;
    }
    setFormOrderKeys(sortedFormsSnapshot.map(getFormKey));
  }, [sortedFormsSnapshot, preserveFormCatalogOrder]);

  const prevUpdateLoaderRef = useRef(false);
  useEffect(() => {
    if (
      prevUpdateLoaderRef.current &&
      !spParticipantComplianceUpdateLoader &&
      !spParticipantComplianceSettingUpdateError
    ) {
      setPreserveFormCatalogOrder(false);
      setFormOrderKeys(null);
    }
    prevUpdateLoaderRef.current = spParticipantComplianceUpdateLoader;
  }, [
    spParticipantComplianceUpdateLoader,
    spParticipantComplianceSettingUpdateError,
  ]);

  useEffect(() => {
    setPreserveFormCatalogOrder(false);
    setFormOrderKeys(null);
  }, [formCategoryFilter]);

  const filteredDocuments = useMemo(() => {
    const query = documentSearch.trim().toLowerCase();
    if (!query) return documents;
    return documents.filter((doc) =>
      (doc.name || "").toLowerCase().includes(query)
    );
  }, [documents, documentSearch]);

  const filteredFormCount = useMemo(
    () =>
      buildDisplayForms(
        catalogForms,
        compliancesData.form || [],
        hasFormFilter,
        formSearch,
        preserveFormCatalogOrder,
        preserveFormCatalogOrder ? formOrderKeys : null
      ).length,
    [
      catalogForms,
      compliancesData.form,
      hasFormFilter,
      formSearch,
      preserveFormCatalogOrder,
      formOrderKeys,
    ]
  );

  const handleDocumentReorder = (activeId, overId) => {
    const docs = [...documents];
    const oldIndex = docs.findIndex((d) => d.id === activeId);
    const newIndex = docs.findIndex((d) => d.id === overId);
    if (oldIndex === -1 || newIndex === -1) return;

    const newDocuments = arrayMove(docs, oldIndex, newIndex);
    setCompliancesData((prev) => ({ ...prev, document: newDocuments }));
    spParticipantComplianceSettingsUpdateRequest(
      buildComplianceUpdatePayload(newDocuments, compliancesData.form || []),
      profileUuid
    );
  };

  const handleDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDragEnd = (event) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    handleDocumentReorder(active.id, over.id);
  };

  const handleAddDocument = () => {
    setAddDocumentModalOpen(true);
  };

  const handleAddDocumentSubmit = (payload) => {
    if (addDocumentSubmittingRef.current) return;
    addDocumentSubmittingRef.current = true;
    setIsAddDocumentSubmitting(true);
    dispatch(spParticipantComplianceAddDocumentRequest(payload, profileUuid));
  };

  const handleDeleteDocumentClick = (item) => {
    if (!item?.uuid) return;
    setDeleteDocumentConfirmItem({ uuid: item.uuid, name: item.name });
  };
  const handleDeleteDocumentConfirm = (confirmed) => {
    if (confirmed && deleteDocumentConfirmItem?.uuid) {
      const uuid = deleteDocumentConfirmItem.uuid;
      setDeleteDocumentLoadingId(uuid);
      dispatch(spParticipantComplianceDeleteDocumentRequest(uuid, profileUuid));
    }
    setDeleteDocumentConfirmItem(null);
  };

  const handleEditDocumentClick = (item) => {
    setEditDocumentItem(item);
  };

  const handleEditDocumentSave = (name, score, isCompliance) => {
    if (!editDocumentItem?.uuid || !profileUuid) return;
    const trimmedName = (name || "").trim();
    if (!trimmedName) return;
    if (editDocumentSubmittingRef.current) return;

    editDocumentSubmittingRef.current = true;
    setIsEditDocumentSubmitting(true);
    spParticipantComplianceUpdateDocumentRequest(
      editDocumentItem.uuid,
      {
        name: trimmedName,
        score: Math.round(Number(score) || 0),
        is_compliance: isCompliance,
      },
      profileUuid
    );
  };

  return (
    <div>
      <div className="min-h-full">
        <SideBarNew />
        <div
          className={`${
            sideBarPadding ? "lg:pl-[4rem]" : "lg:pl-64"
          } flex flex-col duration-300`}
        >
          <Header
            title={
              profileUuid
                ? `Participant Compliance — ${profileName}`
                : "Participant Compliance Settings"
            }
            breadcrumb={
              <Breadcrumb
                pages={SP_SETTING_BREADCRUMB(
                  SETTINGS_BREADCRUMB_PAGES.PARTICIPANT_COMPLIANCE_SETTING
                )}
              />
            }
            btnComponentBeforeRegions={
              <div className="flex items-center gap-2">
                <CustomButton
                  type="button"
                  variant="secondary"
                  clickHandler={() => setCreateOpen(true)}
                  label="Add New Compliance"
                  Icon={<BsPlusLg className="w-4 h-4" />}
                />
                {profileUuid && (
                  <CustomButton
                    type="button"
                    variant="primary"
                    clickHandler={handleAddDocument}
                    label="Add New Document"
                    Icon={<BsPlusLg className="w-4 h-4" />}
                  />
                )}
              </div>
            }
          />
          <section className="w-full sm:px-6 lg:px-8 py-6 lg:py-8">
            {awaitingProfileSelection ? (
              <div className="flex flex-col w-full min-h-[50vh] bg-gray-50 items-center justify-center">
                <CircularLoader
                  classes="flex justify-center items-center"
                  classloader="loader-l"
                />
              </div>
            ) : hasNoProfiles ? (
              <div className="max-w-2xl mx-auto text-center py-16">
                <p className="text-sm text-gray-600 mb-4">
                  No compliance templates yet. Create one to configure participant
                  onboarding documents and forms.
                </p>
                <CustomButton
                  type="button"
                  variant="primary"
                  clickHandler={() => setCreateOpen(true)}
                  label="Add New Compliance"
                  Icon={<BsPlusLg className="w-4 h-4" />}
                />
              </div>
            ) : spParticipantComplianceLoader ? (
              <div className="flex flex-col w-full min-h-[50vh] bg-gray-50 items-center justify-center">
                <CircularLoader
                  classes="flex justify-center items-center"
                  classloader="loader-l"
                />
              </div>
            ) : (
              <div className="w-full space-y-6">
                <div
                  className="sticky z-20 flex flex-col lg:flex-row gap-4 items-stretch py-2 -mx-1 px-1 bg-gray-50"
                  style={{ top: "5.5rem" }}
                >
                  <div className="lg:w-72 xl:w-80 flex-shrink-0">
                    <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden h-full flex flex-col">
                      <div className="px-4 py-3.5 bg-gray-50/80 border-b border-gray-100">
                        <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                          Compliance template
                        </label>
                        <SelectOption
                          isSearch={false}
                          data={profiles}
                          selectedOption={selectedProfileOption ?? null}
                          handleChange={handleProfileChange}
                          placeHolder="Select compliance"
                          loading={profilesLoading}
                          isDisabled={profilesLoading || profiles.length === 0}
                          useMenuPortal
                        />
                      </div>
                      {profileUuid && selectedProfileOption && (
                        <div className="px-4 py-3.5 flex-1 flex flex-col gap-3">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {isDefaultProfile && (
                              <span className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                                Default
                              </span>
                            )}
                            {assignedCount > 0 ? (
                              <span className="inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                                Assigned to {assignedCount} participant
                                {assignedCount === 1 ? "" : "s"}
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600">
                                Not assigned
                              </span>
                            )}
                          </div>
                          {!isDefaultProfile ? (
                            <div className="flex gap-2 mt-auto">
                              <button
                                type="button"
                                onClick={openEditProfile}
                                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 hover:border-indigo-300 transition-colors"
                              >
                                <BsPencil className="w-3.5 h-3.5 flex-shrink-0" />
                                Edit Compliance
                              </button>
                              <span
                                className={`inline-flex ${
                                  isDeleteDisabled ? "cursor-not-allowed" : ""
                                }`}
                                title={deleteTooltip}
                              >
                                <button
                                  type="button"
                                  onClick={handleDeleteProfileClick}
                                  disabled={isDeleteDisabled}
                                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 hover:border-red-300 transition-colors disabled:opacity-50 disabled:hover:bg-white disabled:hover:border-red-200 disabled:cursor-not-allowed"
                                >
                                  <BsTrash className="w-3.5 h-3.5 flex-shrink-0" />
                                  Delete
                                </button>
                              </span>
                            </div>
                          ) : (
                            <p className="mt-auto text-xs text-gray-500 leading-relaxed">
                              Default compliance cannot be edited or deleted.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <InstructionCard
                      totalPercentage={calculateTotalPercentage()}
                      isValid={!checkIsAnyComplianceTrueWithZeroScore()}
                      subjectLabel="participant"
                      sticky={false}
                      dragHint="Drag documents to reorder."
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <h3 className="text-base font-semibold text-gray-900">
                    Participant Onboarding
                  </h3>
                  <p className="text-sm text-gray-500 mt-1 mb-6">
                    Configure mandatory documents and forms for participant onboarding.
                  </p>

                  <div className="w-full rounded-lg border border-gray-200 overflow-hidden">
                    <div
                      className="flex items-center px-5 py-4 bg-gray-50/80 border-b border-gray-200 cursor-pointer select-none w-full"
                      onClick={() => setDocumentsExpanded((prev) => !prev)}
                    >
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDocumentsExpanded((prev) => !prev);
                        }}
                        className="flex-shrink-0 p-0.5 text-gray-500 hover:text-indigo-600 rounded"
                      >
                        {documentsExpanded ? (
                          <BsChevronDown className="w-4 h-4" />
                        ) : (
                          <BsChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      <BsFolder2 className="w-5 h-5 text-indigo-600" />
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                        Documents
                      </h4>
                    </div>
                    </div>
                    {documentsExpanded && (
                    <div className="p-4 bg-white border-t-0 relative">
                      <div className="mb-4 px-1">
                        <label
                          htmlFor="participant-document-search"
                          className="block text-xs font-medium text-gray-600 mb-1.5"
                        >
                          Search documents
                        </label>
                        <Search
                          search={documentSearch}
                          setSearch={setDocumentSearch}
                          placeholder="e.g. NDIS Plan, Service Agreement"
                          customClassSearch="w-full h-10 border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm"
                          width="w-full"
                        />
                        {hasDocumentFilter && (
                          <p className="mt-2 text-xs text-gray-500 tabular-nums">
                            {filteredDocuments.length}{" "}
                            {filteredDocuments.length === 1
                              ? "document"
                              : "documents"}{" "}
                            found
                          </p>
                        )}
                      </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={pointerWithin}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                  >
                    <ParticipantComplianceDocumentsSection
                      items={filteredDocuments}
                      complianceType="document"
                      lastErrorLocation={lastErrorLocation}
                      onSwitchChange={(item, value, type) =>
                        handleSwitchChange("document", item, value, type)
                      }
                      onScoreChange={(item, value) =>
                        handleSwitchChange("document", item, value, "score")
                      }
                      onLastErrorChange={setLastErrorLocation}
                      onAddDocument={handleAddDocument}
                      onDeleteDocument={handleDeleteDocumentClick}
                      onEditDocument={handleEditDocumentClick}
                      deleteDocumentLoadingId={deleteDocumentLoadingId}
                      showDateRequirements
                      mandatorySubtitle="Mandatory for all participants"
                      onDateRequirementChange={handleDateRequirementChange}
                      emptyMessage={
                        hasDocumentFilter
                          ? "No documents match your search."
                          : undefined
                      }
                    />
                    <DragOverlay dropAnimation={null}>
                      <ParticipantComplianceDocumentDragOverlay
                        activeItem={
                          activeDragId
                            ? documents.find((d) => d.id === activeDragId)
                            : null
                        }
                      />
                    </DragOverlay>
                  </DndContext>
                    </div>
                    )}
                  </div>

                  <div className="w-full rounded-lg border border-gray-200 overflow-hidden mt-4">
                    <div
                      className="flex items-center gap-2 px-5 py-4 bg-gray-50/80 border-b border-gray-200 cursor-pointer select-none"
                      onClick={() => setFormsExpanded((prev) => !prev)}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFormsExpanded((prev) => !prev);
                        }}
                        className="flex-shrink-0 p-0.5 text-gray-500 hover:text-indigo-600 rounded"
                      >
                        {formsExpanded ? (
                          <BsChevronDown className="w-4 h-4" />
                        ) : (
                          <BsChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-900">
                        Forms
                      </h4>
                      {(compliancesData.form || []).filter((f) =>
                        parseBooleanVal(f.is_compliance)
                      ).length > 0 && (
                        <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 tabular-nums">
                          {(compliancesData.form || []).filter((f) =>
                            parseBooleanVal(f.is_compliance)
                          ).length}{" "}
                          mandatory
                        </span>
                      )}
                    </div>
                    {formsExpanded && (
                    <div className="flex flex-col">
                    <ParticipantFormFilters
                      search={formSearch}
                      setSearch={setFormSearch}
                      selectedCategories={formCategoryFilter}
                      setSelectedCategories={setFormCategoryFilter}
                      categoryOptions={formCategoryOptions}
                      catalogLoading={catalogLoading}
                      hasActiveFilter={hasFormFilter}
                      resultCount={
                        hasFormFilter ? filteredFormCount : null
                      }
                      onClear={() => {
                        setFormSearch("");
                        setFormCategoryFilter([]);
                      }}
                    />
                    <div className="bg-white border-t border-gray-100">
                    <ParticipantFormsSection
                      complianceForms={compliancesData.form || []}
                      catalogForms={catalogForms}
                      catalogLoading={catalogLoading}
                      hasActiveFilter={hasFormFilter}
                      searchTerm={formSearch}
                      preserveCatalogOrder={preserveFormCatalogOrder}
                      stableOrderKeys={preserveFormCatalogOrder ? formOrderKeys : null}
                      lastErrorLocation={lastErrorLocation}
                      onSwitchChange={(item, value, type) =>
                        handleSwitchChange("form", item, value, type)
                      }
                      onScoreChange={(item, value) =>
                        handleSwitchChange("form", item, value, "score")
                      }
                      onLastErrorChange={setLastErrorLocation}
                      mandatorySubtitle="Mandatory for all participants"
                    />
                    </div>
                    </div>
                    )}
                  </div>
                </div>

                <div className="sticky bottom-0 z-30 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-6 mt-6 border-t border-gray-200 bg-white/95 backdrop-blur-sm rounded-lg px-6 py-5 shadow-[0_-4px_16px_rgba(15,23,42,0.08)]">
                  <ComplianceSummaryBar
                    totalPercentage={calculateTotalPercentage()}
                    isValid={!checkIsAnyComplianceTrueWithZeroScore()}
                  />
                  <div className="flex justify-end gap-2 shrink-0">
                    {/* Discard button – commented out per product request
                    <button
                      type="button"
                      onClick={handleDiscard}
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors py-2 px-3"
                    >
                      Discard
                    </button>
                    */}
                    <CustomButton
                      showLoading={spParticipantComplianceUpdateLoader}
                      isDisabled={isSubmitDisabled}
                      variant="primary"
                      clickHandler={handleSubmit}
                      label="Update Changes"
                    />
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <AddDocumentModal
        isOpen={addDocumentModalOpen}
        onClose={() => setAddDocumentModalOpen(false)}
        onSubmit={handleAddDocumentSubmit}
        isLoading={isAddDocumentSubmitting}
        hideCategoryField
      />
      <EditDocumentModal
        isOpen={!!editDocumentItem}
        onClose={() => setEditDocumentItem(null)}
        document={editDocumentItem}
        onSave={handleEditDocumentSave}
        isLoading={isEditDocumentSubmitting}
        hideCategoryField
      />
      <SweetAlertConfirmationModal
        isOpen={!!deleteDocumentConfirmItem}
        title="Delete document?"
        content={
          deleteDocumentConfirmItem
            ? `Are you sure you want to delete "${deleteDocumentConfirmItem.name}"? This action cannot be undone.`
            : ""
        }
        setIsConfirm={handleDeleteDocumentConfirm}
        dangerMode={true}
        onlyOkBtn={["No, Cancel", "Yes, Delete"]}
      />
      <ModalSkeleton
        isOpen={createOpen}
        title="Add New Compliance"
        closeModal={() => !creating && setCreateOpen(false)}
        modalFooter={null}
        cssClass="w-full max-w-md"
      >
        <div className="space-y-4 p-1">
          <InputField
            label="Compliance name"
            name="compliance_name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. SIL Compliance"
          />
          <ComplianceProfileDescriptionField
            id="compliance_description"
            name="compliance_description"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="text-sm text-gray-600 px-3 py-2"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Cancel
            </button>
            <CustomButton
              variant="primary"
              label="Create"
              isDisabled={!newName.trim() || creating}
              showLoading={creating}
              clickHandler={handleCreateCompliance}
            />
          </div>
        </div>
      </ModalSkeleton>

      <ModalSkeleton
        isOpen={editProfileOpen}
        title={
          isDefaultProfile
            ? "Default Compliance Profile"
            : "Edit Compliance Profile"
        }
        closeModal={() => !savingProfile && setEditProfileOpen(false)}
        modalFooter={null}
        cssClass="w-full max-w-md"
      >
        <div className="space-y-4 p-1">
          {isDefaultProfile && (
            <p className="text-sm text-gray-600 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
              The default compliance template name and description are fixed and
              cannot be edited.
            </p>
          )}
          <InputField
            label="Compliance name"
            name="edit_compliance_name"
            value={editProfileName}
            onChange={(e) => setEditProfileName(e.target.value)}
            isDisabled={isDefaultProfile}
            readOnly={isDefaultProfile}
          />
          <ComplianceProfileDescriptionField
            id="edit_compliance_description"
            name="edit_compliance_description"
            value={editProfileDescription}
            onChange={(e) => setEditProfileDescription(e.target.value)}
            disabled={isDefaultProfile}
            readOnly={isDefaultProfile}
          />
          <label
            className={`flex items-center gap-2 text-sm text-gray-700 ${
              isDefaultProfile ? "opacity-60 cursor-not-allowed" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={editProfileIsDefault}
              disabled={isDefaultProfile}
              onChange={(e) => setEditProfileIsDefault(e.target.checked)}
            />
            Set as default for new participants
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="text-sm text-gray-600 px-3 py-2"
              onClick={() => setEditProfileOpen(false)}
              disabled={savingProfile}
            >
              {isDefaultProfile ? "Close" : "Cancel"}
            </button>
            {!isDefaultProfile && (
              <CustomButton
                variant="primary"
                label="Save"
                isDisabled={!editProfileName.trim() || savingProfile}
                showLoading={savingProfile}
                clickHandler={handleSaveProfile}
              />
            )}
          </div>
        </div>
      </ModalSkeleton>

      <SweetAlertConfirmationModal
        isOpen={!!deleteProfileWarning}
        title={
          deleteProfileWarning?.can_delete === false
            ? "Cannot delete profile"
            : "Delete compliance profile?"
        }
        content={
          deleteProfileWarning
            ? deleteProfileWarning.warning ||
              (deleteProfileWarning.can_delete === false
                ? "This compliance profile cannot be deleted."
                : "Are you sure you want to delete this compliance profile? This action cannot be undone.")
            : ""
        }
        setIsConfirm={handleDeleteProfileConfirm}
        dangerMode={deleteProfileWarning?.can_delete !== false}
        onlyOkBtn={
          deleteProfileWarning?.can_delete === false
            ? "OK"
            : ["No, Cancel", "Yes, Delete"]
        }
      />

    </div>
  );
};

export default ParticipantCompliance;

